import { observer, useLocalObservable } from "mobx-react";
import { Form, Modal, Input, Spin, Alert, Select, Tag, Button, Popconfirm, DatePicker, Space, Radio } from "antd";
import { translateKey as t } from "../../../../../../locales/translation";
import { ClientsController } from "../../../ClientsController";
import { AppStore } from "~/stores/AppStore";
import { useEffect } from "react";
import dayjs from "dayjs";
import _ from "lodash";
type AlertType = "success" | "info" | "warning" | "error";

const LoadScenarioSnapshotModal = observer(({ store, controller }: { store?: AppStore; controller?: ClientsController }) => {
  const [form] = Form.useForm();

  useEffect(() => {});
  const localStore = useLocalObservable(() => ({
    errorMessage: "",
    loadButtonDisabled: false,
    errorType: "error" as AlertType,
    removeAffectedSnapshot: null,
    formIsValid: false,
    confirmOpen: false,
    setRemoveAffectedSnapshot(value) {
      this.removeAffectedSnapshot = value;
    },
    setConfirmOpen(message) {
      this.confirmOpen = message;
    },
    setLoadButtonDisabled(value) {
      this.loadButtonDisabled = value;
    },
    setError(message, type) {
      this.errorMessage = message;
      this.errorType = type;
    },
    setDepotId(passedDepotId) {
      form.setFieldsValue({
        depotId: passedDepotId,
      });
    },
    updateFormValidity() {
      const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
      this.formIsValid = !hasErrors && form.isFieldsTouched(true);
    },
  }));

  const { visible, loading, spinMessage } = controller.loadScenarioSnapshotModal;

  const handleOk = async () => {
    try {
      localStore.setConfirmOpen(false);
      const values = await form.validateFields();
      controller.setLoadScenarioSnapshotModal("spinMessage", "Fetching Scenario Snapshot");
      controller.setLoadScenarioSnapshotModal("loading", true);

      const data = await controller.getScenarioSnapshot(values.scenario);

      controller.setLoadScenarioSnapshotModal("spinMessage", "Updating Scenario");

      await controller.updateScenarioSnapshot(values, data, localStore?.removeAffectedSnapshot);

      handleClose();
    } catch (error) {
      localStore.setError(error.response?.data?.errMessage || error.message, "error");
      if (error.response?.data?.affectedSnapshot) {
        localStore.setRemoveAffectedSnapshot(true);
        localStore.setLoadButtonDisabled(true);
        localStore.setError(error.response.data.errMessage, "warning");
      }
    } finally {
      controller.setLoadScenarioSnapshotModal("loading", false);
    }
  };

  const handleClose = () => {
    localStore.setConfirmOpen(false);
    form.resetFields();
    localStore.setError("", "error");
    controller.setLoadScenarioSnapshotModal(null, {
      visible: false,
      loading: false,
      clientRow: null,
      spinMessage: "",
    });
    localStore.setLoadButtonDisabled(false);
    localStore.setRemoveAffectedSnapshot(null);
    controller.setDepots([]);
    controller.setScenarioSnapshots([]);
  };

  const dropdownFilterOption = (inp: string, opt: any) => opt.props.children?.toString().toLowerCase().indexOf(inp.toLowerCase()) >= 0;

  const onScenarioSelect = (selectedSnapshot) => {
    const { scenarioSnapshots } = controller;

    let foundScenario = scenarioSnapshots.find((sc) => sc._id === selectedSnapshot);
    if (foundScenario?.depotId) {
      localStore.setDepotId(foundScenario.depotId);
    }
  };

  const renderForm = () => {
    const { scenarioSnapshots, formatText } = controller;
    return (
      <div className="flex justify-center items-center">
        <Form form={form} layout="vertical" className="w-full" onFieldsChange={localStore.updateFormValidity}>
          <Form.Item label="Scenario" name="scenario" rules={[{ required: true, message: "Select Scenario" }]}>
            <Select placeholder="Select Scenario" className="w-full" onChange={onScenarioSelect}>
              {scenarioSnapshots.map((scenario) => (
                <Select.Option key={scenario._id} value={scenario._id}>
                  {scenario.ref}
                  <span style={{ float: "right" }}>
                    {scenario?.type ? <Tag color="blue">{formatText(scenario.type)}</Tag> : null}
                    <Tag color="blue">{scenario.client}</Tag>
                    <Tag color="blue">{scenario.depot.depotRef + " - " + scenario.depot.name}</Tag>
                    <Tag color="blue">{dayjs(_.get(scenario, "date")).format("DD/MM/YYYY")}</Tag>
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Target Depot" name="depotId" rules={[{ required: true, message: "Please select an depot" }]}>
            <Select allowClear showSearch={true} style={{ width: "100%", textAlign: "left" }} filterOption={(input, option) => dropdownFilterOption(input, option)}>
              {controller.depots.map((depot) => {
                return (
                  <Select.Option key={depot._id} value={depot._id}>
                    {depot.depotRef + " - " + depot.name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item label="Target Date" name="targetDate" rules={[{ required: true, message: "Select Date" }]} className="flex-1">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          {localStore.errorMessage && (
            <Alert
              message={localStore.errorMessage}
              type={localStore.errorType}
              showIcon
              action={
                localStore.errorType === "warning" ? (
                  <Space direction="vertical">
                    <Button size="small" type="primary" onClick={handleOk}>
                      Move To Target
                    </Button>
                  </Space>
                ) : null
              }
            />
          )}
        </Form>
      </div>
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      title="Load Scenario Snapshot"
      styles={{
        header: { borderBottom: "1px solid #0505050f", paddingBottom: 10 },
      }}
      open={visible}
      onCancel={handleClose}
      centered
      okButtonProps={{ disabled: !localStore.formIsValid }}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t("lblCancel", "Cancel")}
        </Button>,
        <Popconfirm
          placement="topRight"
          key="ok"
          title="Are you sure?"
          description="Please note that all data for the respective depot/day will be replaced by the loaded snapshot."
          onConfirm={handleOk}
          onCancel={() => localStore.setConfirmOpen(false)}
          okText="Proceed"
          cancelText="Cancel"
          disabled={!localStore.formIsValid || localStore.loadButtonDisabled}
          open={localStore.confirmOpen}
          overlayStyle={{ width: 300 }}
        >
          <Button type="primary" disabled={!localStore.formIsValid || localStore.loadButtonDisabled} onClick={() => localStore.setConfirmOpen(true)}>
            {t("lblLoad", "Load")}
          </Button>
        </Popconfirm>,
      ]}
    >
      <Spin size="large" spinning={loading} tip={spinMessage}>
        {renderForm()}
      </Spin>
    </Modal>
  );
});

export default LoadScenarioSnapshotModal;
